/**
 * ============================================================================
 * useLLMValidation Hook
 *
 * LLM 검증 기능 비즈니스 로직
 *
 * 레거시 js/llm-validation.js의 핵심 동작을 React Hook으로 구현:
 * 1. 에디터 본문 가져오기 (Zustand Store에서)
 * 2. LLM별 프롬프트 템플릿과 본문 결합
 * 3. 클립보드에 복사 (Clipboard API + execCommand 폴백)
 * 4. LLM 사이트를 새 탭에서 열기
 * 5. 성공/에러 메시지 표시
 *
 * @version 1.0.0
 * @since 2026-03-03
 * ============================================================================
 */

import { useCallback } from 'react'
import { useEditContent, useRefContent } from '@/stores'
import { LLM_SERVICES, LLM_PROMPTS } from '@/data/llm-prompts'
import type { LLMServiceType } from '@/data/llm-prompts'

// ============================================================================
// 클립보드 복사 유틸리티 (레거시 copyToClipboard 1:1 포팅)
// ============================================================================

/**
 * 텍스트를 클립보드에 복사
 * Clipboard API를 우선 사용하고, 미지원 환경에서는 execCommand 폴백
 *
 * @param text - 복사할 텍스트
 * @throws 클립보드 복사 실패 시 에러 발생
 */
async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Clipboard API 사용 (HTTPS 환경)
      await navigator.clipboard.writeText(text)
      console.log('[LLMValidation] 클립보드 복사 성공 (Clipboard API)')
    } else {
      // 폴백: execCommand 방식
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      console.log('[LLMValidation] 클립보드 복사 성공 (execCommand)')
    }
  } catch (error) {
    console.error('[LLMValidation] 클립보드 복사 실패:', error)
    throw error
  }
}

// ============================================================================
// useLLMValidation Hook
// ============================================================================

/**
 * LLM 검증 Hook 반환 타입
 */
interface UseLLMValidationReturn {
  /** LLM 검증 실행 (프롬프트 생성 → 클립보드 복사 → 사이트 열기 → 메시지) */
  validateWithLLM: (llmService: LLMServiceType, panelType: 'edit' | 'reference') => Promise<void>
}

/**
 * LLM 검증 기능 Custom Hook
 *
 * 에디터 패널의 본문 내용을 LLM 프롬프트와 결합하여
 * 클립보드에 복사하고, 해당 LLM 사이트를 새 탭에서 엽니다.
 *
 * @returns validateWithLLM 함수
 */
export function useLLMValidation(): UseLLMValidationReturn {
  // Zustand Store에서 에디터 콘텐츠 구독
  const editContent = useEditContent()
  const refContent = useRefContent()

  // ==========================================================================
  // validateWithLLM: LLM 검증 핵심 로직
  // 레거시 validatePanelWithLLM() 1:1 포팅
  // ==========================================================================
  const validateWithLLM = useCallback(async (
    llmService: LLMServiceType,
    panelType: 'edit' | 'reference'
  ): Promise<void> => {
    console.log('[LLMValidation] 검증 시작:', { llmService, panelType })

    try {
      // ① 패널 타입에 따라 본문 선택
      const content = panelType === 'edit' ? editContent : refContent
      const panelLabel = panelType === 'edit' ? '수정/작성 글' : '레퍼런스 글'

      // ② 텍스트 비어있는지 검사
      if (!content?.trim()) {
        alert(`${panelLabel}이 비어있습니다. 먼저 글을 작성해주세요.`)
        return
      }

      // ③ LLM 서비스 정보 + 프롬프트 가져오기
      const llmInfo = LLM_SERVICES[llmService]
      const prompt = LLM_PROMPTS[llmService]

      if (!llmInfo || !prompt) {
        console.error('[LLMValidation] 지원하지 않는 LLM 서비스:', llmService)
        alert('지원하지 않는 LLM 서비스입니다.')
        return
      }

      // ④ 프롬프트 + 본문 조합
      const fullText = `${prompt}\n\n${content}`

      console.log('[LLMValidation] 검증 텍스트 생성:', {
        llmService,
        panelType,
        contentLength: content.length,
      })

      // ⑤ 클립보드에 복사
      await copyToClipboard(fullText)

      // ⑥ LLM 사이트 새 탭 열기
      window.open(llmInfo.url, '_blank', 'noopener,noreferrer')

      // ⑦ 성공 메시지 표시
      alert(`${llmInfo.icon} ${llmInfo.name} 페이지가 열렸습니다. Ctrl+V로 붙여넣기하세요!`)

    } catch (error) {
      console.error('[LLMValidation] 검증 실행 실패:', error)
      alert('LLM 검증 실행에 실패했습니다.')
    }
  }, [editContent, refContent])

  return { validateWithLLM }
}

export default useLLMValidation
